import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Archive, Heart, Target, Gem, ArrowRight, Star, Sparkles, ImageOff } from "lucide-react";
import ReleaseCard from "../components/ReleaseCard";
import AiRecommendations from "../components/AiRecommendations";
import AdminAnnouncementsPanel from "../components/AdminAnnouncementsPanel";
import AdminSuggestionsPanel from "../components/AdminSuggestionsPanel";
import SchedulerStatusPanel from "../components/SchedulerStatusPanel";
import ValuationCard from "../components/ValuationCard";
import StatsPanel from "../components/StatsPanel";
import OnboardingPanel from "../components/OnboardingPanel";
import SimilarCollectorsPanel from "../components/SimilarCollectorsPanel";
import BrandCatalogImportDialog from "../components/BrandCatalogImportDialog";
import BatchFillImagesDialog from "../components/BatchFillImagesDialog";
import { toast } from "sonner";

function CollectionShowcase({ recently, navigate }) {
  if (!recently?.length) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-zinc-100">Your Collection</h2>
        <button onClick={() => navigate("/collection")} className="text-xs text-rose-500 hover:text-rose-400 flex items-center gap-1">
          View All <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        {recently.map((entry) => {
          const r = entry.release || {};
          const userPhoto = (entry.photos || [])[0];
          const imgSrc = userPhoto ? resolveImageUrl(userPhoto.url) : (r.model_image ? resolveImageUrl(r.model_image) : null);
          return (
            <div key={entry.entry_id} onClick={() => navigate(`/catalog/${r.release_id || entry.release_id}`)} className="group cursor-pointer bg-zinc-900 border border-zinc-800/50 rounded-xl overflow-hidden hover:border-zinc-700 transition-all hover:-translate-y-0.5">
              <div className="aspect-square bg-zinc-800 overflow-hidden relative">
                {imgSrc ? (
                  <img src={imgSrc} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <ImageOff className="w-6 h-6 text-zinc-700" />
                    <span className="text-[8px] text-zinc-700 font-mono mt-1">{r.khmg_number}</span>
                  </div>
                )}
              </div>
              <div className="p-2">
                <p className="text-[10px] text-zinc-200 font-medium truncate">{r.release_name}</p>
                <p className="text-[9px] text-zinc-600">{r.vehicle}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const missingReleases = useMemo(() =>
    (stats?.missing_preview || []).map((r) => ({ ...r, owned: false, wishlisted: false })),
    [stats?.missing_preview]
  );

  const handleAddCollection = async (release) => {
    try {
      await api.post("/collection", { release_id: release.release_id });
      toast.success("Added to collection!");
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleAddWishlist = async (release) => {
    try {
      await api.post("/wishlist", { release_id: release.release_id });
      toast.success("Added to wishlist!");
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-zinc-900 rounded-xl animate-pulse" />)}
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div data-testid="dashboard-page" className="p-4 sm:p-6 lg:p-8 space-y-8 animate-fadeIn">
      {/* Onboarding (shows only until user completes setup or dismisses) */}
      <OnboardingPanel />

      {/* Year-in-Review CTA */}
      {stats.total_owned >= 3 && (
        <button
          onClick={() => navigate("/wrapped")}
          data-testid="dashboard-wrapped-cta"
          className="group relative w-full overflow-hidden rounded-2xl p-5 sm:p-6 text-left transition-transform hover:-translate-y-0.5"
          style={{ background: "linear-gradient(110deg, #1e0b3a 0%, #4c1d95 35%, #be185d 70%, #f59e0b 100%)" }}
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-3xl group-hover:bg-white/20 transition-colors" />
          <div className="relative flex items-center justify-between gap-4">
            <div>
              <p className="text-amber-200/90 text-[11px] uppercase tracking-[0.25em] font-semibold flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> New · Limited drop
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-black text-white mt-2 leading-tight">
                Your Castfolio Wrapped is here
              </h2>
              <p className="text-white/80 text-sm mt-1">A stylized recap of your last 365 days. Save it. Share it.</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-semibold text-sm shadow-lg group-hover:bg-white/95">
              View Wrapped <ArrowRight className="w-4 h-4" />
            </div>
            <div className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white text-black">
              <ArrowRight className="w-5 h-5" />
            </div>
          </div>
        </button>
      )}

      {/* Welcome + Completion Hero */}
      <div className="bg-gradient-to-r from-zinc-900/80 via-rose-950/20 to-zinc-900/80 border border-white/5 rounded-2xl p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="text-zinc-500 text-sm">Welcome back,</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-100 mt-1">{user?.name || "Collector"}</h1>
            <div className="flex items-center gap-6 mt-4">
              <div>
                <p className="font-mono text-3xl font-bold text-rose-500">{stats.total_owned}</p>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Models</p>
              </div>
              <div className="h-10 w-px bg-zinc-800" />
              <div>
                <p className="font-mono text-3xl font-bold text-emerald-400">{stats.completion_percentage}%</p>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Complete</p>
              </div>
              <div className="h-10 w-px bg-zinc-800" />
              <div>
                <p className="font-mono text-3xl font-bold text-amber-400">{stats.wishlist_count}</p>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Wanted</p>
              </div>
            </div>
          </div>
          <div className="sm:w-48">
            <div className="relative">
              <Progress value={stats.completion_percentage} className="h-3 bg-zinc-800" />
              <p className="text-xs text-zinc-500 mt-2 text-right">{stats.total_owned} / {stats.total_releases}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Collection Showcase — visual first */}
      <CollectionShowcase recently={stats.recently_added} navigate={navigate} />

      {/* Gamification — XP, level, streak, badges */}
      <StatsPanel />

      {/* Similar collectors recommendations */}
      <SimilarCollectorsPanel limit={6} />

      {/* Valuation snapshot + sparkline */}
      <ValuationCard />

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
          <Gem className="w-4 h-4 text-emerald-400 mb-1" />
          <p className="font-mono text-lg font-bold text-emerald-400">${stats.estimated_collection_value?.toLocaleString() || 0}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Collection Value</p>
        </div>
        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
          <Target className="w-4 h-4 text-blue-400 mb-1" />
          <p className="font-mono text-lg font-bold text-blue-400">{stats.total_missing}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Missing</p>
        </div>
        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
          <Sparkles className="w-4 h-4 text-amber-400 mb-1" />
          <p className="font-mono text-lg font-bold text-amber-400">{stats.total_purchases || stats.total_owned}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Purchases</p>
        </div>
        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
          <Archive className="w-4 h-4 text-zinc-400 mb-1" />
          <p className="font-mono text-lg font-bold text-zinc-300">${stats.total_spent?.toLocaleString() || 0}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Invested</p>
        </div>
      </div>

      {/* Two Column: Series Completion + Most Valuable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
          <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4">Series Progress</h2>
          <div className="space-y-3">
            {stats.series_completion?.map((s) => (
              <div key={s.series}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-300">{s.series}</span>
                  <span className="font-mono text-xs text-zinc-500">{s.owned}/{s.total}</span>
                </div>
                <Progress value={s.percentage} className="h-1.5 bg-zinc-800" />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
          <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4 flex items-center gap-2">
            <Gem className="w-4 h-4 text-amber-400" /> Most Valuable
          </h2>
          {stats.most_valuable?.length > 0 ? (
            <div className="space-y-2">
              {stats.most_valuable.map((r, i) => (
                <div key={r.release_id} onClick={() => navigate(`/catalog/${r.release_id}`)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 cursor-pointer transition-colors">
                  <span className="text-xs font-mono text-zinc-600 w-5">#{i + 1}</span>
                  {r.model_image ? (
                    <img src={resolveImageUrl(r.model_image)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-[8px] text-zinc-600 font-mono">{r.khmg_number}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200 truncate">{r.release_name}</p>
                    <p className="text-[11px] text-zinc-500">{r.vehicle}</p>
                  </div>
                  <span className="font-mono text-sm text-emerald-400 font-semibold">{r.estimated_market_value != null ? `$${r.estimated_market_value}` : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm">Add models to see your top values</p>
          )}
        </div>
      </div>

      {/* Grail Wishlist */}
      {stats.wishlist_grails?.length > 0 && (
        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-zinc-100 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400" /> Grail Wishlist
            </h2>
            <button onClick={() => navigate("/wishlist")} className="text-xs text-rose-500 hover:text-rose-400 flex items-center gap-1">View All <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.wishlist_grails.map((g) => (
              <div key={g.wishlist_id} onClick={() => navigate(`/catalog/${g.release_id}`)} className="flex items-center gap-3 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800/50 hover:border-zinc-700 cursor-pointer transition-colors">
                {g.release?.model_image ? (
                  <img src={resolveImageUrl(g.release.model_image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-zinc-800 flex items-center justify-center text-[8px] text-zinc-600 font-mono">{g.release?.khmg_number}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-200 truncate font-medium">{g.release?.release_name}</p>
                  <p className="text-[11px] text-zinc-500">{g.release?.vehicle}</p>
                  <span className="font-mono text-xs text-rose-500">{g.release?.estimated_market_value != null ? `$${g.release.estimated_market_value}` : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Panel */}
      {user?.role === "admin" && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="admin-toolbar">
          <BrandCatalogImportDialog />
          <BatchFillImagesDialog />
        </div>
      )}
      <AdminAnnouncementsPanel />
      <AdminSuggestionsPanel />
      <SchedulerStatusPanel />

      {/* AI Recommendations */}
      <AiRecommendations />

      {/* Missing Preview */}
      {stats.missing_preview?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-zinc-100">Next to Collect</h2>
            <button onClick={() => navigate("/missing")} className="text-xs text-rose-500 hover:text-rose-400 flex items-center gap-1">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {missingReleases.map((r) => (
              <ReleaseCard key={r.release_id} release={r} isMissing onAddCollection={handleAddCollection} onAddWishlist={handleAddWishlist} onCardClick={(id) => navigate(`/catalog/${id}`)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
